import React from 'react'
import { Box } from '@mui/material'
import { MasterManagementToolSelect } from 'shared/ui'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { nodeAttributionLeaves } from './utility/TreeHelpers'
import { allProductsByPrimaryTreeId } from 'shared/api/masters'
import { MigrateSchools } from './tree/masters/MigrateSchools'
import { Versions } from './tree/masters/Versions'
import { MasterUpdateValidation } from './tree/masters/MasterUpdateValidation'
import { EditableCopyView } from './EditableCopyView'
import { TrackedCopiesTree } from './tree/trackedCopies/TrackedCopies'
import { ManageMetadata } from './resources/products/ManageMetadata'
import { SupportedPlatformFeatures } from './resources/products/SupportedPlatformFeatures'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useAllPartnerNames } from 'shared/hooks/partners.hooks'

export function useCheckTreeAttributions(ids: {
  treeId: string
  versionId: string
}) {
  const attributions = useAllPartnerNames()
  const { tree } = useGetTreeById(ids)
  if (!tree) {
    return
  }
  const attributionLeaves = nodeAttributionLeaves(tree)
  const treeAttributions = attributionLeaves.map(({ body: { value } }) => value)
  return {
    attributions: treeAttributions,
    tree,
    valid:
      treeAttributions.length === 1 &&
      attributions?.includes(treeAttributions[0]),
  }
}

export enum MasterManagementTools {
  migrate = 'Migrate',
  versions = 'Versions',
  updateValidation = 'Master Update Validation',
  editableCopy = 'Editable Copy',
  trackedCopies = 'Tracked Copies',
  manageMetadata = 'Manage Metadata',
  supportedPlatformFeatures = 'Supported Platform Features',
}

function useMasterTitle(masterId: string) {
  const product = useAtomValue(allProductsByPrimaryTreeId)[masterId]
  return product?.title ?? ''
}

function useMasterManagementTools(masterId: string) {
  const product = useAtomValue(allProductsByPrimaryTreeId)[masterId]
  if (!product) return []

  return [
    {
      title: MasterManagementTools.migrate,
      component: () => <MigrateSchools productId={product.id} />,
    },
    {
      title: MasterManagementTools.versions,
      component: () => <Versions productId={product.id} />,
    },
    {
      title: MasterManagementTools.updateValidation,
      component: () => <MasterUpdateValidation productId={product.id} />,
    },
    {
      title: MasterManagementTools.editableCopy,
      component: () => <EditableCopyView productId={product.id} />,
    },
    {
      title: MasterManagementTools.trackedCopies,
      component: () => <TrackedCopiesTree productId={product.id} />,
    },
    {
      title: MasterManagementTools.manageMetadata,
      component: () => <ManageMetadata productId={product.id} />,
    },
    {
      title: MasterManagementTools.supportedPlatformFeatures,
      component: () => <SupportedPlatformFeatures productId={product.id} />,
    },
  ]
}

export function MasterManagement({
  masterId,
  onComponentSelect,
}: {
  masterId: string
  onComponentSelect?: (title?: string) => void
}) {
  const title = useMasterTitle(masterId)
  const components = useMasterManagementTools(masterId)
  return (
    <Box>
      <Box>
        <strong>{title}</strong>
      </Box>
      <Box padding="10px">
        <MasterManagementToolSelect
          components={components}
          onComponentSelect={onComponentSelect}
        />
      </Box>
    </Box>
  )
}
